import React, { useState } from 'react';
import { Mic2, Music2, Zap, CheckCircle, Send, Star, Clock, Shield } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { branding } from '../config/branding';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const packages = [
  {
    name: 'Mastering Only',
    price: '$75',
    turnaround: '24–48 hrs',
    description: 'Already mixed? Get a professional master that translates everywhere.',
    features: [
      'Loudness optimization (streaming-ready)',
      'EQ & dynamic balancing',
      'Stereo widening',
      '2 revisions included',
      'WAV + MP3 delivery',
    ],
    icon: Star,
    highlight: false,
  },
  {
    name: 'Mix + Master',
    price: '$175',
    turnaround: '48–72 hrs',
    description: 'Full treatment — stems in, radio-ready out.',
    features: [
      'Full stem mixing',
      'Vocal tuning & timing',
      'Professional mastering',
      '3 revisions included',
      'WAV + MP3 delivery',
      'Stems returned clean',
    ],
    icon: Zap,
    highlight: true,
  },
  {
    name: 'Mixing Only',
    price: '$100',
    turnaround: '48–72 hrs',
    description: 'Send your stems, get back a tight, balanced mix.',
    features: [
      'Full stem mix',
      'Vocal processing & effects',
      'Instrument balancing & EQ',
      '2 revisions included',
      'WAV delivery',
    ],
    icon: Music2,
    highlight: false,
  },
];

const MixMaster = () => {
  const { currentTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    artistName: '',
    service: 'Mix + Master',
    genre: 'Hip-Hop',
    details: '',
    referenceLinks: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: `[Mix/Master Booking] ${formData.service} — ${formData.artistName || formData.name}`,
          message: `Service: ${formData.service}\nArtist: ${formData.artistName}\nGenre: ${formData.genre}\n\nProject Details:\n${formData.details}\n\nReference Links: ${formData.referenceLinks || 'N/A'}`,
          type: 'booking',
        }),
      });
      if (res.ok) {
        toast({
          title: '🎚️ Booking Request Sent!',
          description: "We'll hit you back within 24 hours with next steps.",
        });
        setFormData({ name: '', email: '', artistName: '', service: 'Mix + Master', genre: 'Hip-Hop', details: '', referenceLinks: '' });
      } else {
        throw new Error('Failed');
      }
    } catch {
      toast({ title: 'Something went wrong', description: 'Try again or email us directly.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-4">

        {/* Hero */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 animate-pulse"
            style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.accent || currentTheme.primary}bb)` }}
          >
            <Mic2 className="w-10 h-10 text-black" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Mix & Master</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Self-taught. Battle-tested. Marz Tha Advocate has been engineering sound for years — your track deserves the real treatment.
          </p>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-6 mb-16">
          {[
            { icon: Clock, label: '24–72 hr turnaround' },
            { icon: Shield, label: 'Revisions included' },
            { icon: CheckCircle, label: 'Streaming-ready delivery' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-gray-400 text-sm">
              <Icon className="w-4 h-4" style={{ color: currentTheme.primary }} />
              {label}
            </div>
          ))}
        </div>

        {/* Packages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {packages.map((pkg) => {
            const Icon = pkg.icon;
            return (
              <div
                key={pkg.name}
                onClick={() => setFormData((p) => ({ ...p, service: pkg.name }))}
                className={`relative rounded-2xl p-8 border cursor-pointer transition-all duration-300 hover:scale-105 ${
                  pkg.highlight
                    ? 'border-2 bg-zinc-900'
                    : 'border-white/10 bg-zinc-900/50'
                }`}
                style={pkg.highlight ? { borderColor: currentTheme.primary } : {}}
              >
                {pkg.highlight && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-black"
                    style={{ backgroundColor: currentTheme.primary }}
                  >
                    MOST POPULAR
                  </div>
                )}
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${currentTheme.primary}20` }}
                >
                  <Icon className="w-6 h-6" style={{ color: currentTheme.primary }} />
                </div>
                <h3 className="text-white text-xl font-bold mb-1">{pkg.name}</h3>
                <div className="text-3xl font-bold mb-1" style={{ color: currentTheme.primary }}>{pkg.price}</div>
                <div className="text-gray-500 text-sm mb-3">⏱ {pkg.turnaround}</div>
                <p className="text-gray-400 text-sm mb-5">{pkg.description}</p>
                <ul className="space-y-2">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: currentTheme.primary }} />
                      {f}
                    </li>
                  ))}
                </ul>
                {formData.service === pkg.name && (
                  <div
                    className="mt-4 text-center text-xs font-bold py-1 rounded-full"
                    style={{ backgroundColor: `${currentTheme.primary}30`, color: currentTheme.primary }}
                  >
                    ✓ Selected
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Booking Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-2">Book Your Session</h2>
            <p className="text-gray-400 text-sm mb-6">Fill out the form and you'll hear back within 24 hours with file upload instructions and invoice.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Your Name *</label>
                  <input
                    type="text" name="name" value={formData.name} onChange={handleChange} required
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-white/30 transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Email *</label>
                  <input
                    type="email" name="email" value={formData.email} onChange={handleChange} required
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-white/30 transition-all"
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">Artist/Stage Name</label>
                <input
                  type="text" name="artistName" value={formData.artistName} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-white/30 transition-all"
                  placeholder="Your artist name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Service *</label>
                  <select
                    name="service" value={formData.service} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white focus:outline-none focus:border-white/30 transition-all"
                  >
                    <option>Mastering Only — $75</option>
                    <option>Mixing Only — $100</option>
                    <option>Mix + Master — $175</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Genre *</label>
                  <select
                    name="genre" value={formData.genre} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white focus:outline-none focus:border-white/30 transition-all"
                  >
                    {['Hip-Hop', 'Trap', 'Boom Bap', 'Drill', 'R&B', 'Lo-Fi', 'Pop', 'Other'].map((g) => (
                      <option key={g}>{g}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">Project Details *</label>
                <textarea
                  name="details" value={formData.details} onChange={handleChange} required rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-white/30 transition-all resize-none"
                  placeholder="Tell me about the track — vibe, energy, what you're going for, any reference tracks..."
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">Reference Tracks / Links (optional)</label>
                <input
                  type="text" name="referenceLinks" value={formData.referenceLinks} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-white/30 transition-all"
                  placeholder="YouTube, SoundCloud, Spotify links..."
                />
              </div>

              <Button
                type="submit" disabled={submitting}
                className="w-full h-12 text-black font-bold text-lg hover:scale-105 transition-transform disabled:opacity-50"
                style={{ backgroundColor: currentTheme.primary }}
              >
                <Send className="w-5 h-5 mr-2" />
                {submitting ? 'Sending...' : 'Book My Session'}
              </Button>

              <p className="text-gray-500 text-xs text-center">
                You'll receive file upload instructions & invoice within 24 hours.
              </p>
            </form>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-4xl mx-auto mt-20">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">FAQ</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { q: 'What format should I send my files?', a: 'WAV or AIFF preferred, 24-bit/44.1kHz or higher. For mixing, send individual stems organized clearly.' },
              { q: 'How do I send my files?', a: "After you book, you'll get a link to upload your stems via Google Drive or WeTransfer." },
              { q: 'How many revisions do I get?', a: 'Mastering gets 2 revisions, mixing packages get 2–3. Additional revisions are available at $25/round.' },
              { q: 'Do you do rush orders?', a: '24-hour rush is available for +$50. Mention it in your project details.' },
            ].map((faq) => (
              <div key={faq.q} className="p-6 rounded-xl bg-zinc-900/50 border border-white/10">
                <h3 className="text-white font-bold mb-2">{faq.q}</h3>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default MixMaster;
